import authService from './authService';
import {
  Event,
  EventFormData,
  CRMPerson,
  DeliveryOrder,
  AttendedFilter,
  DeliveryOrderAttendanceSummary,
  DeliveryOrderDispatchResponse,
  DeliveryOrderVolume,
  DeliveryOrderFreightQuote,
  DeliveryOrderTracking,
  RoutePlanningResponse,
} from '../types/event';
import { PaginatedResponse, ApiResponse } from '../types/common';

// Módulo Eventos: eventos, convites (Pedidos de Entrega), busca de convidados no CRM,
// cotação de frete e planejamento de rota.
class EventService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ── Eventos ────────────────────────────────────────────────────────────────
  async listEvents(page = 1, limit = 20, search?: string, isActive?: boolean): Promise<PaginatedResponse<Event>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { q: search }),
      ...(isActive !== undefined && { is_active: isActive.toString() }),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/events?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar eventos');
    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data) ? json.data : [],
      pagination: {
        page: json.pagination?.page ?? 1,
        limit: json.pagination?.page_size ?? limit,
        total: json.pagination?.total_items ?? 0,
        totalPages: json.pagination?.total_pages ?? 1,
      },
    };
  }

  async getEvent(id: number): Promise<Event> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/events/${id}`);
    if (!response.ok) throw new Error('Evento não encontrado');
    const data: ApiResponse<Event> = await response.json();
    return data.data!;
  }

  async createEvent(form: EventFormData): Promise<Event> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/events', form);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao criar evento');
    }
    const data: ApiResponse<Event> = await response.json();
    return data.data!;
  }

  async updateEvent(id: number, form: EventFormData): Promise<Event> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/events/${id}`, form);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar evento');
    }
    const data: ApiResponse<Event> = await response.json();
    return data.data!;
  }

  // ── Convidados (CRM) ───────────────────────────────────────────────────────
  async searchGuests(query: string, limit = 10): Promise<CRMPerson[]> {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/events/guest-search?${params}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao consultar o CRM');
    }
    const data: ApiResponse<CRMPerson[]> = await response.json();
    return data.data ?? [];
  }

  // ── Convites (Pedidos de Entrega) ─────────────────────────────────────────
  async listDeliveryOrders(opts: {
    page?: number;
    limit?: number;
    eventId?: number;
    statusId?: number;
    search?: string;
    attended?: AttendedFilter;
  }): Promise<PaginatedResponse<DeliveryOrder> & { summary?: DeliveryOrderAttendanceSummary }> {
    const params = new URLSearchParams({
      page: (opts.page ?? 1).toString(),
      limit: (opts.limit ?? 20).toString(),
      ...(opts.eventId && { event_id: opts.eventId.toString() }),
      ...(opts.statusId && { status_id: opts.statusId.toString() }),
      ...(opts.search && { q: opts.search }),
      ...(opts.attended && { attended: opts.attended }),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar convites');
    const json = await response.json();
    return {
      success: true,
      data: Array.isArray(json.data) ? json.data : [],
      pagination: {
        page: json.pagination?.page ?? 1,
        limit: json.pagination?.page_size ?? (opts.limit ?? 20),
        total: json.pagination?.total_items ?? 0,
        totalPages: json.pagination?.total_pages ?? 1,
      },
      // Contadores da listagem inteira. O backend degrada para ausente se a consulta falhar
      // — a listagem continua válida, a tela só não mostra os números.
      summary: json.summary ?? undefined,
    };
  }

  /**
   * Marca o comparecimento do convidado ao evento direto da listagem.
   * `attended = null` volta o convite para "não informado" — precisa do campo próprio
   * `clear_attended` porque `attended: null` chega ao backend como "não informado nesta
   * requisição", indistinguível de ausente (mesmo motivo do `unlink_invoice`).
   */
  async setDeliveryOrderAttendance(id: number, attended: boolean | null): Promise<DeliveryOrder> {
    return this.updateDeliveryOrder(id, attended === null ? { clear_attended: true } : { attended });
  }

  async getDeliveryOrder(id: number): Promise<DeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/${id}`);
    if (!response.ok) throw new Error('Convite não encontrado');
    const data: ApiResponse<DeliveryOrder> = await response.json();
    return data.data!;
  }

  async createDeliveryOrder(payload: Partial<DeliveryOrder> & { volume?: Partial<DeliveryOrderVolume> }): Promise<DeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/delivery-orders', payload);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao criar convite');
    }
    const data: ApiResponse<DeliveryOrder> = await response.json();
    return data.data!;
  }

  async updateDeliveryOrder(id: number, payload: Record<string, unknown>): Promise<DeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/delivery-orders/${id}`, payload);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar convite');
    }
    const data: ApiResponse<DeliveryOrder> = await response.json();
    return data.data!;
  }

  /**
   * Despacho Correios em 1 clique: emite a NF de bonificação e cria a pré-postagem na
   * modalidade da cotação selecionada. Idempotente — reclicar retoma do passo que faltou.
   */
  async dispatchCorreios(id: number): Promise<DeliveryOrderDispatchResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/delivery-orders/${id}/dispatch-correios`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha no despacho do convite pelos Correios');
    }
    const data: ApiResponse<DeliveryOrderDispatchResponse> = await response.json();
    return data.data!;
  }

  /**
   * Baixa o PDF único de impressão do convite: etiqueta da NF (DANFE Simplificada) +
   * etiquetas dos Correios, todas térmicas ~100×150 mm.
   */
  async downloadInvitationLabels(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/${id}/labels`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao baixar as etiquetas do convite');
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiquetas-convite-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async getVolumes(id: number): Promise<DeliveryOrderVolume[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/${id}/volumes`);
    if (!response.ok) throw new Error('Falha ao buscar volumes do convite');
    const data: ApiResponse<DeliveryOrderVolume[]> = await response.json();
    return data.data ?? [];
  }

  async updateVolume(id: number, volumeId: number, volume: Omit<DeliveryOrderVolume, 'delivery_order_volume_id' | 'delivery_order_id' | 'volume_number'>): Promise<DeliveryOrderVolume[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/delivery-orders/${id}/volumes/${volumeId}`, volume);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao atualizar volume');
    }
    const data: ApiResponse<DeliveryOrderVolume[]> = await response.json();
    return data.data ?? [];
  }

  // ── Cotação de frete (mesmo painel do pedido de venda) ────────────────────
  async getFreightQuotes(id: number): Promise<DeliveryOrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/${id}/freight-quotes`);
    if (!response.ok) throw new Error('Falha ao buscar cotações de frete');
    const data: ApiResponse<DeliveryOrderFreightQuote[]> = await response.json();
    return data.data ?? [];
  }

  async calculateFreightQuotes(id: number): Promise<DeliveryOrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/delivery-orders/${id}/freight-quotes/calculate`, {});
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao calcular cotações de frete');
    }
    const data: ApiResponse<DeliveryOrderFreightQuote[]> = await response.json();
    return data.data ?? [];
  }

  async selectFreightQuote(id: number, quoteId: number): Promise<DeliveryOrderFreightQuote[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/delivery-orders/${id}/freight-quotes/select`, {
      delivery_order_freight_quote_id: quoteId,
      is_selected: true,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Falha ao selecionar cotação de frete');
    }
    const data: ApiResponse<DeliveryOrderFreightQuote[]> = await response.json();
    return data.data ?? [];
  }

  async deleteFreightQuotes(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/delivery-orders/${id}/freight-quotes`);
    if (!response.ok) throw new Error('Falha ao limpar cotações de frete');
  }

  // ── Rastreio / planejamento ────────────────────────────────────────────────
  async getTracking(id: number): Promise<DeliveryOrderTracking> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/${id}/tracking`);
    if (!response.ok) throw new Error('Falha ao buscar rastreio do convite');
    const data: ApiResponse<DeliveryOrderTracking> = await response.json();
    return data.data!;
  }

  async getPendingForRoute(eventId?: number): Promise<DeliveryOrder[]> {
    const params = new URLSearchParams(eventId ? { event_id: eventId.toString() } : {});
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/delivery-orders/pending-for-route?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar convites pendentes de rota');
    const data: ApiResponse<DeliveryOrder[]> = await response.json();
    return data.data ?? [];
  }

  async getRoutePlanning(eventId: number, city?: string): Promise<RoutePlanningResponse> {
    const params = new URLSearchParams(city ? { city } : {});
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/events/${eventId}/route-planning?${params}`);
    if (!response.ok) throw new Error('Falha ao carregar o planejamento de rota');
    const data: ApiResponse<RoutePlanningResponse> = await response.json();
    return data.data!;
  }
}

export default new EventService();
